import { isProduction } from '@glow/common'
import { AnyAction } from 'redux'
import { ThunkDispatch as ReduxThunkDispatch, ThunkAction } from 'redux-thunk'
import { api, ConsumedResponse, QueryData } from '../../http/httpHelper'
import { httpDone } from '../../reducers/httpStatus'
import { SessionUser } from '../../types/coreEntitiesTypes'
import { AppStateType } from '../../utils/appStateReduxStore'
import { ActionType, ActionTypeWithAccess } from '../actionTypes'

export interface AnyData {
  [key: string]: any
}
export type ThunkResult<R> = ThunkAction<R, AppStateType, null, AnyAction>
export type ThunkDispatch = ReduxThunkDispatch<AppStateType, null, AnyAction>

export const fetchIfNotPresent: (
  actionType: ActionType,
  queryBody: QueryData,
  user?: SessionUser
) => ThunkResult<Promise<ConsumedResponse>> =
  (actionType: ActionType, queryBody: QueryData) => (dispatch, getState) => {
    if (httpDone(getState, actionType, queryBody)) {
      return Promise.resolve(new Response(null, { status: 200 })) as Promise<ConsumedResponse>
    } else {
      return dispatch(api.asyncQuery(actionType, queryBody))
    }
  }

export const fetchIfNotPresentWithAccess: (
  actionType: ActionTypeWithAccess,
  queryBody: QueryData,
  user: SessionUser
) => ThunkResult<Promise<ConsumedResponse>> =
  (actionType: ActionTypeWithAccess, queryBody: QueryData, user: SessionUser) => (dispatch, getState) => {
    if (httpDone(getState, actionType, queryBody)) {
      return Promise.resolve(new Response(null, { status: 200 })) as Promise<ConsumedResponse>
    } else if (
      !user.get('access')?.some((access) => actionType.userAccessKeys.includes(access)) ||
      (isProduction() && !actionType.roles.includes(user.get('role')))
    ) {
      // User doesn't have access to call this query
      console.warn(
        `user with role: ${user.get('role')} and access ${user.get('access')?.join(', ')} tried calling query ${JSON.stringify(queryBody)}`
      )
      return Promise.resolve(new Response(null, { status: 200 })) as Promise<ConsumedResponse>
    } else {
      return dispatch(api.asyncQuery(actionType, queryBody))
    }
  }

export const fetchEvenIfPresent: (
  actionType: ActionType,
  queryBody: QueryData
) => ThunkResult<Promise<ConsumedResponse>> = (actionType: ActionType, queryBody: QueryData) => (dispatch) =>
  dispatch(api.asyncQuery(actionType, queryBody))

const inFlightRequests = new Map<string, Promise<ConsumedResponse>>()

export const fetchIfNotInFlight: (
  actionType: ActionType,
  queryBody: QueryData
) => ThunkResult<Promise<ConsumedResponse>> = (actionType: ActionType, queryBody: QueryData) => (dispatch) => {
  const key = `${actionType.name}:${JSON.stringify(queryBody.query)}:${JSON.stringify(queryBody.params)}`
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key)!
  }
  const promise = dispatch(api.asyncQuery(actionType, queryBody)) as Promise<ConsumedResponse>
  inFlightRequests.set(key, promise)
  // Swallowing here only keeps the bookkeeping chain from becoming an unhandled rejection of its
  // own. The promise handed back to the caller still rejects.
  promise.catch(() => {}).finally(() => inFlightRequests.delete(key))
  return promise
}
